// Is the on-screen keyboard open? There is no browser event for it, so it is read from the visual viewport. The rules are
// Hope's (ai-rate-finder ChatComponent.keyboardOpenFromViewport, from real-device fixes: FRU-21810, iPhone SE on iOS 15.4,
// Android Chrome's offsetTop pan). iOS draws the keyboard over the layout viewport (innerHeight does not shrink), Android
// Chrome shrinks the visual viewport or pans it, and some browsers only show a drop against the height seen at focus.

export interface KeyboardViewportState {
  /** window.innerHeight */
  innerHeight: number;
  /** visualViewport.height */
  visualHeight: number;
  /** visualViewport.offsetTop */
  offsetTop: number;
  /** (hover: none) and (pointer: coarse) */
  coarsePointer: boolean;
  /** The tallest viewport seen while a text field was focused on a touch device; 0 when none yet. */
  baselineHeight: number;
}

export function keyboardOpenFromViewport(s: KeyboardViewportState): boolean {
  if (!s.innerHeight || !s.visualHeight) { return false; }
  return s.visualHeight < s.innerHeight - 80
    || (s.offsetTop + s.visualHeight) < s.innerHeight - 80
    || (s.coarsePointer && s.baselineHeight > 0 && s.baselineHeight - s.visualHeight > 140);
}

function isTextField(el: any): boolean {
  return !!el && /^(input|textarea)$/i.test(el.tagName || '');
}

/**
 * Calls onChange(open) whenever the keyboard opens or closes, and returns a function that stops watching.
 * It never calls onChange with the state it already reported. A browser without visualViewport reports nothing.
 */
export function watchKeyboardOpen(onChange: (open: boolean) => void, win: Window = window): () => void {
  const vv = win.visualViewport;
  if (!vv) { return () => {}; }
  const doc = win.document;
  let baseline = 0;
  let open = false;
  let blurTimer: any = null;

  const coarse = (): boolean => {
    try { return !!(win.matchMedia && win.matchMedia('(hover: none) and (pointer: coarse)').matches); } catch (e) { return false; }
  };
  const update = (): void => {
    const now = keyboardOpenFromViewport({
      innerHeight: win.innerHeight || 0,
      visualHeight: Number(vv.height) || 0,
      offsetTop: Number(vv.offsetTop) || 0,
      coarsePointer: coarse(),
      baselineHeight: baseline,
    });
    if (now !== open) {
      open = now;
      onChange(open);
    }
  };
  const onFocusIn = (e: Event): void => {
    if (blurTimer) { clearTimeout(blurTimer); blurTimer = null; }
    // The height seen as a field takes focus, before the keyboard has shrunk anything, is what later drops are measured against.
    if (coarse() && isTextField(e.target)) {
      const h = Math.round(Math.max(win.innerHeight || 0, Number(vv.height) || 0));
      if (h > baseline) { baseline = h; }
    }
    update();
  };
  const onFocusOut = (): void => {
    // Focus moves between fields through a moment with nothing focused: only a field left for good clears the baseline.
    blurTimer = setTimeout(() => {
      blurTimer = null;
      if (!isTextField(doc.activeElement)) { baseline = 0; }
      update();
    }, 0);
  };

  vv.addEventListener('resize', update);
  vv.addEventListener('scroll', update);
  doc.addEventListener('focusin', onFocusIn);
  doc.addEventListener('focusout', onFocusOut);
  update();
  return () => {
    if (blurTimer) { clearTimeout(blurTimer); }
    vv.removeEventListener('resize', update);
    vv.removeEventListener('scroll', update);
    doc.removeEventListener('focusin', onFocusIn);
    doc.removeEventListener('focusout', onFocusOut);
  };
}
